/* eslint-disable react/prop-types */
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

// DATA
import { accountsSelectors } from 'app-components/flux/accounts';
import { rootUiActions } from 'app-components/flux/root-ui';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
} from 'app-components/flux/scheduled-transactions';
import { tracker } from 'app-components/utils/core';

// CUSTOM
import ScheduledTransactionEditDialog from 'components/ScheduledTransactions/ScheduledTransactionEditDialog';

import TransactionItemSelectDialog from 'components/TransactionItemSelect/TransactionItemSelectDialog';
import { getLesserBilledTxnIds } from 'data/transactionList/selectors';
import ReviewDialog from './ReviewDialog';
import { DIALOG_TYPE } from './actions';

const typeEnums = scheduledTransactionsTypes.STTypeEnum;

const TertiaryNode = (props) => {
  const { onTransactionSelected } = props;
  return (
    <Typography
      variant='paragraphRegular'
      sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', color: '#666666' }}
    >
      {"Couldn't Find?"}
      <Button
        id='add-manually-transaction'
        onClick={() => onTransactionSelected && onTransactionSelected()}
        sx={{ color: 'secondary.main', marginLeft: '6px' }}
      >
        ADD MANUALLY
      </Button>
    </Typography>
  );
};

// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Dialog Component                   - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function ScheduledTransactionAddDialog(props) {
  const { confirmation = true, dialogType } = props;
  const dispatch = useDispatch();
  const models = useSelector(scheduledTransactionsSelectors.getScheduledTransactions);

  const [open, setOpen] = useState(true);

  const [manualClient, setManualClient] = useState(undefined);
  const [waitingClient, setWaitingClient] = useState(undefined);
  const [createdEdit, setCreatedEdit] = useState(undefined);
  const accounts = useSelector(accountsSelectors.getTransactionAccounts);
  const accountIds = useMemo(() => accounts.keySeq(), [accounts]);

  let createdModel;
  const finalClientId = waitingClient || manualClient;
  if (finalClientId) {
    createdModel = models.find((model) => model.clientId === finalClientId);
  }

  const defaultModel = {
    clientId: manualClient,
    transaction: { isBill: dialogType === typeEnums.BILL, isSubscription: dialogType === typeEnums.SUBSCRIPTION },
  };

  const closeDialog = () => {
    setOpen(false);
    dispatch(rootUiActions.removeDialog(DIALOG_TYPE));
  };

  const selectTransaction = (transaction) => {
    const clientId = uuidv4().toUpperCase();
    if (transaction?.id) {
      dispatch(
        scheduledTransactionsActions.createBillScoutSchedule(
          { clientId, id: transaction.id },
          { props: { isSubscription: dialogType === typeEnums.SUBSCRIPTION } },
        ),
      );
      setWaitingClient(clientId);
      if (!confirmation) {
        closeDialog();
        // returnFunc?.(clientId);
      }
    } else {
      setManualClient(clientId);
    }
    // eslint-disable-next-line no-nested-ternary
    const type =
      dialogType === typeEnums.BILL
        ? 'bills'
        : dialogType === typeEnums.INCOME
          ? 'income'
          : dialogType === typeEnums.SUBSCRIPTION
            ? 'subscriptions'
            : dialogType === typeEnums.TRANSFER
              ? 'transfers'
              : undefined;
    if (type) {
      tracker.track(tracker.events.editSpendingPlanBucket, { location: type, field: 'add' });
    }
  };

  const createSubmit = () => {
    setWaitingClient(waitingClient || manualClient);
    setCreatedEdit(false);
  };

  const goBack = () => {
    if (createdEdit) {
      setCreatedEdit(false);
    } else {
      setManualClient(undefined);
    }
  };

  const edit = () => {
    setCreatedEdit(true);
  };

  let isBill;
  let isTransfer = false;
  let title;
  if (dialogType === typeEnums.BILL) {
    isBill = true;
    title = 'Add bill';
  } else if (dialogType === typeEnums.SUBSCRIPTION) {
    isBill = true;
    title = 'Add subscription';
  } else if (dialogType === typeEnums.INCOME) {
    isBill = false;
    title = 'Add income';
  } else if (dialogType === typeEnums.TRANSFER) {
    isTransfer = true;
    isBill = true;
    title = 'Add transfer or credit card payment';
  }

  const txnIds = useSelector((state) => getLesserBilledTxnIds(state, { isBill, isTransfer, accountIds }));

  return (
    <>
      {open && !manualClient && !waitingClient && (
        <TransactionItemSelectDialog
          open
          onClose={closeDialog}
          title={title || 'Add a Bill or Paycheck'}
          txnItemIds={txnIds}
          disableMultipleRowSelection
          showAccountFilter={false}
          primaryButtonText='Create'
          onConfirm={(txns) => {
            selectTransaction(txns[0]?.transaction);
          }}
          tertiaryNode={<TertiaryNode onTransactionSelected={selectTransaction} />}
          subtitle='Choose a transaction to make recurring'
        />
      )}

      {open && (createdEdit || (!createdModel && Boolean(manualClient))) && (
        <ScheduledTransactionEditDialog
          open
          scheduledTransaction={createdModel || defaultModel}
          onSubmit={createSubmit}
          onClose={closeDialog}
          onBack={goBack}
          title={createdEdit ? 'Edit Series' : 'Create Series'}
          subscriptionInitial={dialogType === typeEnums.SUBSCRIPTION}
          incomeInitial={dialogType === typeEnums.INCOME}
          transferInitial={dialogType === typeEnums.TRANSFER}
          sharedcomponentid='SCHEDULED_TRANSACTION_ADD_DIALOG'
        />
      )}

      {open && !createdEdit && Boolean(waitingClient || createdModel) && (
        <ReviewDialog
          open
          model={createdModel}
          onClose={closeDialog}
          edit={edit}
          sharedcomponentid='SCHEDULED_TRANSACTION_ADD_DIALOG'
        />
      )}
    </>
  );
}

ScheduledTransactionAddDialog.propTypes = {
  confirmation: PropTypes.bool,
  dialogType: PropTypes.string,
};

export default ScheduledTransactionAddDialog;
